import React, { useState, useEffect } from 'react';
import { Users, Search, AlertCircle, Settings, Clock, Shield, UserPlus, UsersRound, Download } from 'lucide-react';
import RBACManagement from './RBACManagement';
import AccessRequests from './AccessRequests';
import EmployeeManagement from './EmployeeManagement';
import ManagersManagement from './ManagersManagement';
import UserProfile from './UserProfile';
import BulkUploadManagement from './BulkUploadManagement';

const AdminTab = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSubTab, setActiveSubTab] = useState('overview'); // 'overview', 'employees', 'rbac', 'managers', 'bulk-upload'

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        console.log('AdminTab: Fetching employees...');
        const response = await fetch('/api/employees?limit=1000&offset=0');
        
        console.log('AdminTab: Response status:', response.status);
        
        // 304 is OK - it means we're using cached data
        if (!response.ok && response.status !== 304) {
          throw new Error(`Failed to fetch employee data: ${response.status} ${response.statusText}`);
        }
        
        const allEmployees = await response.json();
        console.log(`AdminTab: Loaded ${allEmployees.length} employees`, allEmployees);
        
        if (!Array.isArray(allEmployees)) {
          throw new Error('API returned invalid data format');
        }
        
        setEmployees(allEmployees);
        setLoading(false);
        console.log('AdminTab: Loading complete');

      } catch (error) {
        console.error('AdminTab: Error loading employees:', error);
        setError(`Failed to load employees: ${error.message}`);
        setLoading(false);
      }
    };

    loadEmployees();
  }, []);

  // Check if this is admin view or manager view
  const simpleAuthUser = sessionStorage.getItem('simpleAuthUser');
  const isAdminView = sessionStorage.getItem('isAdminView') === 'true';
  const loggedInManagerId = simpleAuthUser;

  // Debug logging
  console.log('=== ADMIN TAB DEBUG ===');
  console.log('Logged in Manager ID:', loggedInManagerId);
  console.log('Is Admin View:', isAdminView);
  console.log('Total Employees:', employees.length);
  if (employees.length > 0) {
    console.log('Sample employee managers:', employees.slice(0, 5).map(e => ({ name: e.name, manager: e.manager })));
  }

  const filteredEmployees = employees.filter(emp => {
    // First apply manager filtering (if not admin view)
    const matchesManager = isAdminView ? true : emp.manager === loggedInManagerId;
    
    // Then apply search filtering
    if (!searchQuery) return matchesManager;
    
    const query = searchQuery.toLowerCase();
    const matchesSearch = (
      emp.name.toLowerCase().includes(query) ||
      emp.role.toLowerCase().includes(query) ||
      emp.department.toLowerCase().includes(query) ||
      emp.manager.toLowerCase().includes(query)
    );
    
    return matchesManager && matchesSearch;
  });

  console.log('Filtered employees count:', filteredEmployees.length);

  // Group employees by department for admin view
  const departmentStats = filteredEmployees.reduce((acc, emp) => {
    const dept = emp.department || 'Unassigned';
    if (!acc[dept]) {
      acc[dept] = { count: 0, members: [] };
    }
    acc[dept].count++;
    acc[dept].members.push(emp);
    return acc;
  }, {});

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div className="loading-spinner"></div>
        <div>Loading admin dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: '#fee',
        border: '1px solid #fcc',
        borderRadius: '8px',
        padding: '20px',
        margin: '20px 0',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <AlertCircle size={24} color="#d63031" />
        <div>
          <h3 style={{ margin: '0 0 8px 0', color: '#d63031' }}>Error Loading Data</h3>
          <p style={{ margin: 0, color: '#666' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      {/* Sub-navigation tabs */}
      <div className="sub-tabs" style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '1px solid #e0e0e0',
        paddingBottom: '16px'
      }}>
        <button
          onClick={() => setActiveSubTab('overview')}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: '6px',
            backgroundColor: activeSubTab === 'overview' ? '#6c5ce7' : 'transparent',
            color: activeSubTab === 'overview' ? 'white' : '#666',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <Users size={16} />
          Overview
        </button>
        <button
          onClick={() => setActiveSubTab('employees')}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: '6px',
            backgroundColor: activeSubTab === 'employees' ? '#6c5ce7' : 'transparent',
            color: activeSubTab === 'employees' ? 'white' : '#666',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <UserPlus size={16} />
          Employee Management
        </button>
        <button
          onClick={() => setActiveSubTab('rbac')}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: '6px',
            backgroundColor: activeSubTab === 'rbac' ? '#6c5ce7' : 'transparent',
            color: activeSubTab === 'rbac' ? 'white' : '#666',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <Shield size={16} />
          RBAC Management
        </button>
        <button
          onClick={() => setActiveSubTab('managers')}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: '6px',
            backgroundColor: activeSubTab === 'managers' ? '#6c5ce7' : 'transparent',
            color: activeSubTab === 'managers' ? 'white' : '#666',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <UsersRound size={16} />
          Manage Managers
        </button>
        <button
          onClick={() => setActiveSubTab('bulk-upload')}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: '6px',
            backgroundColor: activeSubTab === 'bulk-upload' ? '#6c5ce7' : 'transparent',
            color: activeSubTab === 'bulk-upload' ? 'white' : '#666',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <Download size={16} />
          Bulk Upload
        </button>
      </div>

      {/* Content based on active sub-tab */}
      {activeSubTab === 'rbac' ? (
        <RBACManagement />
      ) : activeSubTab === 'employees' ? (
        <EmployeeManagement />
      ) : activeSubTab === 'managers' ? (
        <ManagersManagement />
      ) : activeSubTab === 'bulk-upload' ? (
        <BulkUploadManagement />
      ) : (
        <>
          <div className="header">
            <div className="header-text">
              <h1>Admin Dashboard</h1>
              <p>Complete organizational overview and management</p>
            </div>
          </div>

      {/* Search */}
      <div className="search-filters" style={{ marginBottom: '24px' }}>
        <div className="search-container">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="Search all employees by name, role, team..."
            className="search-input"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Department Overview */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e0e0e0',
          background: '#f8f9fa'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>Team Overview</h2>
          <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>
            Complete organizational structure with {filteredEmployees.length} employees
          </p>
        </div>

        <div style={{ padding: '24px' }}>
          {Object.entries(departmentStats)
            .sort(([,a], [,b]) => b.count - a.count)
            .map(([deptName, data]) => (
              <div key={deptName} style={{
                marginBottom: '32px',
                paddingBottom: '24px',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: '18px',
                    color: '#333',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: '#6c5ce7'
                    }}></div>
                    {deptName}
                  </h3>
                  <span style={{
                    background: '#f8f9fa',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#666'
                  }}>
                    {data.count} employees
                  </span>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '12px'
                }}>
                  {data.members.map(member => (
                    <div key={member.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: '#6c5ce7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}>
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontWeight: '500',
                          color: '#333',
                          fontSize: '14px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {member.name}
                        </div>
                        <div style={{
                          color: '#666',
                          fontSize: '12px',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '2px'
                        }}>
                          {member.roles && member.roles.length > 0 ? (
                            member.roles.map((role, index) => (
                              <span
                                key={index}
                                style={{
                                  backgroundColor: '#e3f2fd',
                                  color: '#1976d2',
                                  padding: '1px 4px',
                                  borderRadius: '8px',
                                  fontSize: '10px',
                                  fontWeight: '500'
                                }}
                              >
                                {role}
                              </span>
                            ))
                          ) : (
                            <span>{member.role}</span>
                          )}
                        </div>
                        <div style={{
                          color: '#888',
                          fontSize: '11px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          Manager: {member.manager}
                        </div>
                      </div>
                      <div style={{
                        padding: '4px 8px',
                        background: '#d4edda',
                        color: '#155724',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: '500'
                      }}>
                        Active
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

          {Object.keys(departmentStats).length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#666'
            }}>
              <Users size={64} style={{ opacity: 0.3, marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>No Employees Found</h3>
              <p style={{ margin: 0 }}>
                {searchQuery ? `No employees found matching "${searchQuery}"` : 'No employee data available.'}
              </p>
            </div>
          )}
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default AdminTab;
